import { observer } from 'mobx-react-lite';
import ImageUploader from './image-uploader';
import { imageValidator } from '@/utils/image-validator';
import { request } from '@/common';
import { SVGPureIcon } from '@/ui';
import appLogger from '@public/log/monitor';
import { HelpOther } from '@/common/helper';
import { Button, message } from 'antd';
import { logArrts, namiCoinType, ModifyImageStore } from '../store';
import { useState, ReactNode, useMemo } from 'react';
import { processImageToFitRules } from './process-image-to-fit-rules';
import './index.scss';
import { uploadToS3 } from '@/common/upload/upload-s3';

export default observer<{
  imageUrl: string;
  updateImageUrl: (url: string) => void;
  handleSubmit: () => void;
  text: string;
  className?: string;
  heightExceeded?: boolean;
  disabled?: boolean;
  loading: boolean;
  children?: ReactNode;
  isDivider?: boolean;
}>(
  ({
    imageUrl,
    updateImageUrl,
    text,
    className,
    children,
    disabled = false,
    loading,
    handleSubmit,
    heightExceeded = false,
    isDivider = false,
  }) => {
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);
    const formType = segments.pop();

    const [uploadProgress, setUploadProgress] = useState(0);
    const handleUploaded = async (file: File) => {
      const { data: { url } } = await uploadToS3(file)
      const {
        data: { result },
      } = await request
        .post('/audit/image?appsource=so&device_platform=Web', {
          level: 0,
          image_url: url,
          source: 'figure_image',
        })
      if (!result) {
        throw new Error('audit')
      }
      setUploadProgress(100)
      updateImageUrl(url);
    };

    const onBeforeUpload = (fileList) => {
      appLogger.log({
        key: 'aibot_photo_creation',
        action: 'click',
        arrt: logArrts[formType],
        type: 're_upload',
      });
      fileList.forEach(async (item, index) => {
        if (item.isProcessed) {
          return;
        }
        item.isProcessed = true;

        setUploadProgress(.1);

        try {

          const file = await processImageToFitRules(item, formType)
            .catch(e => {
              console.error(e);
              if (e.message === 'ratio') {
                HelpOther.toast('图片比例不符合要求，请重新上传', 'error')
              } else if (e.message === 'format') {
                HelpOther.toast('图片格式不支持，请重新选择', 'error')
              } else if (e.message === 'size') {
                HelpOther.toast('图片文件大小过大，请重新选择', 'error')
              } else {
                HelpOther.toast('图片获取失败，请重新选择', 'error')
              }
              throw e
            })

          await handleUploaded(file).catch(e => {
            setUploadProgress(0)
            if (e.message === 'audit') {
              HelpOther.toast('图片审核失败，请重新选择', 'error')
            } else {
              HelpOther.toast('上传失败，请重新选择', 'error')
            }
          })
        } finally {
          setTimeout(() => {
            setUploadProgress(0)
          }, 1000);
        }
      });
      // 同步return才能阻止默认行为
      return false;
    };

    const handle = () => {
      if (handleSubmit) {
        handleSubmit();
      }
    };
    // 当前工具纳米消耗
    const namiCost = useMemo(() => {
      const item = ModifyImageStore.nanoCoinList.find(item => item.scene === namiCoinType[formType]);
      return item?.amount || 0
    }, [formType])

    return (
      <div
        className={`modify-image-input m-auto rounded-20px flex items-center gap-20px min-h-112px p-16px bg-[#fff] w-[max-content] ${className || ''} `}
        style={{
          boxShadow: '0px 2px 10px 0px #1D21291A',
        }}
      >
        <div className="w-80px h-80px flex flex-start flex-shrink-0">
          <ImageUploader
            shownImg={imageUrl}
            onBeforeUpload={onBeforeUpload}
            uploadProgress={uploadProgress}
          />
        </div>
        {/* <div className='flex-1 h-full flex'> */}
        {children ? children : <div className='m-auto h-28px w-1px bg-[#1D2129] opacity-10'></div>}
        {/* </div> */}
        {isDivider && (
          <div className="m-auto w-1px h-28px bg-[#1D2129] bg-opacity-30"></div>
        )}

        <div
          className={`flex h-full ${heightExceeded ? 'items-end' : 'items-center'}`}
        >
          {loading ? (
            <Button
              type="primary"
              loading={true}
              disabled={disabled}
              style={{
                minWidth: '144px',
                padding: '0px 32px',
                height: '48px',
                display: 'flex',
                borderRadius: '8px',
              }}
            ></Button>
          ) : (
            <Button
              type="primary"
              loading={loading}
              disabled={disabled}
              onClick={handle}
              className='mouse-active-translate'
              style={{
                minWidth: '144px',
                padding: '0px 32px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
              }}
            >
              <div className={`flex items-center gap-6px`}>
                <SVGPureIcon name="modify-icon" size={16} />
                {text}
                {/* {+namiCost > 1 && `（${namiCost} 纳米）`} */}
              </div>
            </Button>
          )}
        </div>
      </div>
    );
  }
);
